import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import CSVFileValidator from "csv-file-validator";
import { uploadReceiptFile } from "redux/actions/uploadReceiptFileAction";
import { showNotificationDialogManualOrder } from "redux/actions/notificationManualOrderAction";
import { encryptString } from "components/common/EncryptDecrypt";
import {
  UPLOAD_FAILED_EMERGENCYORDER,
  RECEIPT_FILE_SIZE_EXCEEDED,
} from "services/CONSTANTS";
import {
  MAX_FILE_SIZE_BYTES,
  PO_RECEIPT_UPLOAD_CSV_HEADERS,
  baseStyle,
  focusedStyle,
  acceptStyle,
  rejectStyle,
  MESSAGES,
} from "./constants";

export const useDropzoneUpload = ({
  acceptedFiles,
  setAcceptedFiles,
  setFileError,
  setShowUploadingDialog,
}) => {
  const dispatch = useDispatch();
  const loginResponse = useSelector((state) => state.user.loginResponse);

  const onDrop = useCallback(
    (files, fileRejections) => {
      if (files.length) {
        setFileError(null);
      } else {
        const errors = fileRejections.flatMap((x) => x.errors);
        const hasSizeError = errors.some((e) => e.code === "file-too-large");
        const hasTypeError = errors.some((e) => e.code === "file-invalid-type");
        if (hasSizeError) {
          setFileError(RECEIPT_FILE_SIZE_EXCEEDED);
          dispatch(
            showNotificationDialogManualOrder(
              MESSAGES.UPLOAD_FAILED_TITLE,
              RECEIPT_FILE_SIZE_EXCEEDED,
              "Done",
            ),
          );
        } else if (hasTypeError) {
          setFileError(MESSAGES.INVALID_FILE_TYPE);
          dispatch(
            showNotificationDialogManualOrder(
              MESSAGES.UPLOAD_FAILED_TITLE,
              MESSAGES.INVALID_FILE_TYPE,
              "Done",
            ),
          );
        } else {
          setFileError(null);
        }
      }
      setAcceptedFiles([...files]);
    },
    [dispatch, setAcceptedFiles, setFileError],
  );

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    multiple: false,
    maxSize: MAX_FILE_SIZE_BYTES,
    disabled: acceptedFiles.length > 0,
  });

  const dropzoneStyle = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  const removeFile = (file) => {
    const filterOut = (prev) => prev.filter((f) => f !== file);
    return () => setAcceptedFiles(filterOut);
  };

  const validate = () => {
    if (!acceptedFiles.length) {
      setFileError(MESSAGES.INVALID_FILE_EMPTY);
      return false;
    }
    return true;
  };

  const handleUploadFile = (e) => {
    e.preventDefault();
    if (!validate()) return;

    CSVFileValidator(acceptedFiles[0], {
      headers: PO_RECEIPT_UPLOAD_CSV_HEADERS,
    })
      .then(async (csvData) => {
        const { inValidData, data } = csvData;
        if (data.length === 0 || inValidData.length) {
          dispatch(
            showNotificationDialogManualOrder(
              MESSAGES.UPLOAD_FAILED_TITLE,
              MESSAGES.INVALID_FILE_FORMAT,
              "Done",
            ),
          );
          return;
        }
        setShowUploadingDialog(true);
        const encryptedEmailId = await encryptString(loginResponse?.email);
        const formData = new FormData();
        formData.append("file", acceptedFiles[0]);
        formData.append("userId", encryptedEmailId);
        dispatch(
          uploadReceiptFile({ params: formData }),
        );
      })
      .catch(() => {
        dispatch(
          showNotificationDialogManualOrder(
            MESSAGES.UPLOAD_FAILED_TITLE,
            UPLOAD_FAILED_EMERGENCYORDER,
            "Done",
          ),
        );
      });
  };

  return {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragReject,
    dropzoneStyle,
    removeFile,
    handleUploadFile,
  };
};
